import type { CaptureKind } from '@mystiq/contracts';
import type { RecordingsStore, StoredRecording } from './recordings-store.ts';

/** What is recorded: the microphone, or the microphone mixed with the sound of a browser tab. */
export type CaptureSource = 'microphone' | 'tab';

export type RecorderState =
  | { status: 'idle' }
  | { status: 'permission'; source: CaptureSource }
  | { status: 'recording'; elapsedMs: number; paused: boolean; level: number; notice: string | null }
  | { status: 'finalizing' }
  | { status: 'error'; message: string };

export type FinishedRecording = { id: string; blob: Blob; durationMs: number; title: string; filename: string; capture: CaptureKind; uploadKey: string };

export type RecorderOptions = {
  store: RecordingsStore | null;
  workspaceId: string;
  userKey: string;
  capture: CaptureKind;
  onChange(state: RecorderState): void;
  onInterrupted(recording: FinishedRecording, message: string): void;
};

/** The first format this browser can record, as MediaRecorder names it. */
export function recordingFormat(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  return ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'].find((type) => MediaRecorder.isTypeSupported(type)) ?? null;
}

const extensionOf = (mimeType: string) => (mimeType.startsWith('audio/mp4') ? 'm4a' : mimeType.startsWith('audio/ogg') ? 'ogg' : 'webm');
const pad = (value: number) => String(value).padStart(2, '0');

const label = (capture: CaptureKind) => (capture === 'meeting' ? 'Meeting' : 'Voice note');

/** The file name downloads get: "Voice note 2026-10-23 14.05.webm" (sortable). */
export function recordingFilename(capture: CaptureKind, startedAt: number, mimeType: string): string {
  const at = new Date(startedAt);
  const stamp = `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())} ${pad(at.getHours())}.${pad(at.getMinutes())}`;
  return `${label(capture)} ${stamp}.${extensionOf(mimeType)}`;
}

/** The title people see: "Voice note 23 Oct 2026, 14:05", in their own date format. */
export function recordingTitle(capture: CaptureKind, startedAt: number): string {
  return `${label(capture)} ${new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(startedAt)}`;
}

function microphoneProblem(error: unknown): string {
  const name = (error as { name?: string })?.name;
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'Microphone access was blocked. Allow the microphone for this site in your browser, then try again.';
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'No microphone was found. Connect one, then try again.';
  if (name === 'NotReadableError') return 'The microphone is in use by another app or could not be started.';
  return 'The microphone could not be started.';
}

export const newId = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

const lockName = (id: string) => `mystiq-recording-${id}`;

/**
 * Recordings being made right now in any tab of this browser (each holds a
 * lock while it records), so another tab never offers them as cut off.
 */
export async function recordingsInProgress(): Promise<Set<string>> {
  if (!navigator.locks?.query) return new Set();
  const { held = [] } = await navigator.locks.query().catch(() => ({ held: [] as LockInfo[] }));
  return new Set(held.map((lock) => lock.name ?? '').filter((name) => name.startsWith('mystiq-recording-')).map((name) => name.slice('mystiq-recording-'.length)));
}

/**
 * Records in the browser with MediaRecorder. Every second of audio is kept in
 * the browser's store as it arrives, so a closed tab or crash loses at most
 * the last second; a recording cut off that way is offered again on the next
 * visit. When the microphone or recorder stops unexpectedly, what was recorded
 * is kept and handed to onInterrupted.
 */
export class Recorder {
  #state: RecorderState = { status: 'idle' };
  readonly #options: RecorderOptions;
  #streams: MediaStream[] = [];
  #context: AudioContext | null = null;
  #analyser: AnalyserNode | null = null;
  #media: MediaRecorder | null = null;
  #chunks: Blob[] = [];
  #writes: Promise<void> = Promise.resolve();
  #seq = 0;
  #meta: StoredRecording | null = null;
  #accumulated = 0;
  #resumedAt = 0;
  #ticker: ReturnType<typeof setInterval> | undefined;
  #stopping: Promise<FinishedRecording> | null = null;
  #notice: string | null = null;
  #disposed = false;
  #unlock: (() => void) | null = null;

  constructor(options: RecorderOptions) {
    this.#options = options;
  }

  get state(): RecorderState {
    return this.#state;
  }

  /** The recording in progress, if any. */
  get activeId(): string | undefined {
    return this.#meta?.id;
  }

  #set(state: RecorderState) {
    this.#state = state;
    this.#options.onChange(state);
  }

  #elapsed(): number {
    return this.#accumulated + (this.#media?.state === 'recording' ? performance.now() - this.#resumedAt : 0);
  }

  #tick = () => {
    if (this.#state.status !== 'recording') return;
    let level = 0;
    if (this.#analyser && this.#media?.state === 'recording') {
      const samples = new Float32Array(this.#analyser.fftSize);
      this.#analyser.getFloatTimeDomainData(samples);
      level = Math.min(1, Math.sqrt(samples.reduce((sum, value) => sum + value * value, 0) / samples.length) * 4);
    }
    this.#set({ status: 'recording', elapsedMs: this.#elapsed(), paused: this.#media?.state === 'paused', level, notice: this.#notice });
  };

  async start(source: CaptureSource): Promise<void> {
    if (this.#state.status === 'recording' || this.#state.status === 'permission' || this.#state.status === 'finalizing') return;
    const format = recordingFormat();
    if (!format || !navigator.mediaDevices?.getUserMedia) {
      this.#set({ status: 'error', message: 'This browser cannot record audio here. Use a current Chrome, Edge, Firefox or Safari, or upload a recording instead.' });
      return;
    }
    if (source === 'tab' && !navigator.mediaDevices.getDisplayMedia) {
      this.#set({ status: 'error', message: "This browser cannot share a tab's sound. Record the microphone only, or use Chrome or Edge." });
      return;
    }
    this.#set({ status: 'permission', source });
    let microphone: MediaStream;
    try {
      microphone = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
    } catch (error) {
      this.#set({ status: 'error', message: microphoneProblem(error) });
      return;
    }
    this.#streams = [microphone];
    // The page went away while the browser was asking: release the microphone again.
    if (this.#disposed) return this.#release();
    let tabSound: MediaStreamTrack[] = [];
    if (source === 'tab') {
      try {
        // Browsers share a tab's sound together with its picture; only the sound is recorded.
        const display = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
        this.#streams.push(display);
        tabSound = display.getAudioTracks();
        for (const track of display.getVideoTracks()) track.stop();
      } catch {
        this.#release();
        this.#set({ status: 'error', message: 'Sharing was cancelled, so nothing was recorded.' });
        return;
      }
      if (!tabSound.length) {
        this.#release();
        this.#set({ status: 'error', message: "No tab sound was shared. Choose a browser tab and turn on 'Share tab audio', or record the microphone only." });
        return;
      }
      if (this.#disposed) return this.#release();
    }

    // Mix the sources into one track, and measure the microphone's level for the meter.
    let recorded: MediaStream = microphone;
    try {
      this.#context = new AudioContext();
      // Started by the click on Record; resumed in case the browser held it back.
      void this.#context.resume().catch(() => undefined);
      const mic = this.#context.createMediaStreamSource(microphone);
      this.#analyser = this.#context.createAnalyser();
      this.#analyser.fftSize = 1024;
      mic.connect(this.#analyser);
      if (tabSound.length) {
        const mix = this.#context.createMediaStreamDestination();
        mic.connect(mix);
        this.#context.createMediaStreamSource(new MediaStream(tabSound)).connect(mix);
        recorded = mix.stream;
      }
    } catch {
      this.#analyser = null;
    }

    const startedAt = Date.now();
    this.#meta = {
      id: newId(),
      workspaceId: this.#options.workspaceId,
      userKey: this.#options.userKey,
      capture: this.#options.capture,
      title: recordingTitle(this.#options.capture, startedAt),
      filename: recordingFilename(this.#options.capture, startedAt, format),
      mimeType: format,
      startedAt,
      durationMs: 0,
      uploadKey: `recording-${newId()}`,
      state: 'recording',
    };
    this.#chunks = [];
    this.#seq = 0;
    this.#notice = this.#options.store ? null : 'This browser keeps nothing between visits here, so the recording lives in this tab until it is saved.';
    await this.#options.store?.create(this.#meta).catch(() => {
      this.#notice = 'The recording could not be kept in this browser; it lives in this tab until it is saved.';
    });
    if (this.#disposed) {
      await this.#options.store?.remove(this.#meta.id).catch(() => undefined);
      return this.#release();
    }
    // Held while recording, so other tabs see this recording is still going.
    if (navigator.locks?.request) {
      const id = this.#meta.id;
      void navigator.locks
        .request(lockName(id), () => new Promise<void>((release) => (this.#unlock = release)))
        .catch(() => undefined);
    }

    const media = new MediaRecorder(recorded, { mimeType: format, audioBitsPerSecond: 64_000 });
    this.#media = media;
    media.ondataavailable = (event) => {
      if (!event.data.size) return;
      this.#chunks.push(event.data);
      const seq = this.#seq++;
      const id = this.#meta!.id;
      const store = this.#options.store;
      if (!store) return;
      const durationMs = Math.round(this.#elapsed());
      // Every few seconds the length so far, so a recording cut off by a closed tab says how long it is.
      this.#writes = this.#writes
        .then(() => store.addChunk(id, seq, event.data))
        .then(() => (seq % 5 === 4 ? store.update(id, { durationMs }) : undefined))
        .catch(() => undefined);
    };
    media.onerror = () => void this.#interrupt('Recording stopped unexpectedly.');
    microphone.getAudioTracks()[0]?.addEventListener('ended', () => void this.#interrupt('The microphone stopped (it was unplugged, or access was withdrawn).'));
    tabSound[0]?.addEventListener('ended', () => {
      this.#notice = 'The tab stopped sharing its sound; still recording the microphone.';
    });
    media.start(1000);
    this.#accumulated = 0;
    this.#resumedAt = performance.now();
    this.#set({ status: 'recording', elapsedMs: 0, paused: false, level: 0, notice: this.#notice });
    this.#ticker = setInterval(this.#tick, 100);
  }

  pause() {
    if (this.#media?.state !== 'recording') return;
    this.#accumulated += performance.now() - this.#resumedAt;
    this.#media.pause();
    this.#tick();
  }

  resume() {
    if (this.#media?.state !== 'paused') return;
    this.#resumedAt = performance.now();
    this.#media.resume();
    this.#tick();
  }

  /** Stops recording and returns the whole recording (also kept in the browser until it is saved). */
  stop(): Promise<FinishedRecording> {
    if (this.#stopping) return this.#stopping;
    const media = this.#media;
    const meta = this.#meta;
    if (!media || !meta) return Promise.reject(new Error('Nothing is being recorded.'));
    this.#set({ status: 'finalizing' });
    this.#stopping = new Promise<FinishedRecording>((resolve) => {
      const durationMs = Math.round(this.#elapsed());
      const finish = async () => {
        clearInterval(this.#ticker);
        this.#release();
        await this.#writes;
        await this.#options.store?.update(meta.id, { state: 'stopped', durationMs }).catch(() => undefined);
        this.#unlock?.();
        this.#unlock = null;
        resolve({
          id: meta.id,
          blob: new Blob(this.#chunks, { type: meta.mimeType }),
          durationMs,
          title: meta.title ?? meta.filename,
          filename: meta.filename,
          capture: meta.capture,
          uploadKey: meta.uploadKey,
        });
      };
      media.onstop = () => void finish();
      if (media.state === 'inactive') void finish();
      else media.stop();
    });
    return this.#stopping;
  }

  /** Stops and throws the recording away (also from the browser's store). */
  async discard(): Promise<void> {
    const recording = this.#media ? await this.stop() : null;
    if (recording) await this.#options.store?.remove(recording.id).catch(() => undefined);
    this.reset();
  }

  /**
   * The page is going away: a recording in progress is stopped and kept in the
   * browser (offered for saving later); one still waiting for the microphone
   * prompt never starts.
   */
  dispose() {
    this.#disposed = true;
    clearInterval(this.#ticker);
    if (this.#media && this.#media.state !== 'inactive') void this.stop().catch(() => undefined);
  }

  /** Ready for a new recording. */
  reset() {
    this.#media = null;
    this.#meta = null;
    this.#stopping = null;
    this.#chunks = [];
    this.#set({ status: 'idle' });
  }

  async #interrupt(message: string) {
    if (!this.#media || this.#stopping) return;
    const recording = await this.stop();
    this.reset();
    this.#options.onInterrupted(recording, message);
  }

  #release() {
    for (const stream of this.#streams) for (const track of stream.getTracks()) track.stop();
    this.#streams = [];
    void this.#context?.close().catch(() => undefined);
    this.#context = null;
    this.#analyser = null;
  }
}
